import { apiClient } from './client';

// Types
export interface PlanFeatures {
  email_accounts: number;
  email_labels: number;
  sync_interval_mins: number;
  archive_months: number;
  priority_support: boolean;
  api_access: boolean;
  realtime_sync: boolean;
  active_templates: number;
  monthly_documents: number;
  word_export: boolean;
  pdf_export: boolean;
  advanced_logic: boolean;
  team_sharing: boolean;
  custom_variables: boolean;
  users: number;
  storage_gb: number;
  mobile_app: boolean;
  dedicated_support: boolean;
  on_site_training: boolean;
}

export interface Plan {
  id: string;
  module: 'mail' | 'template' | 'bundle';
  tier: 'standard' | 'pro' | 'unlimited';
  name: string;
  description: string;
  monthly_price: number; // Kuruş cinsinden
  yearly_price: number;
  features: PlanFeatures;
  perks: string[];
}

export interface PlansResponse {
  plans: {
    mail: Plan[];
    template: Plan[];
    bundle: Plan[];
  };
}

export interface Subscription {
  id: string;
  module_type: 'mail' | 'template' | 'bundle';
  tier: 'standard' | 'pro' | 'unlimited';
  billing_cycle: 'monthly' | 'yearly';
  status: 'trial' | 'active' | 'canceled' | 'expired' | 'pending';
  start_date: string;
  end_date: string;
  auto_renew: boolean;
  days_left: number;
  plan_name: string;
  is_active: boolean;
}

export interface Payment {
  id: string;
  merchant_oid: string;
  amount: number;
  currency: string;
  module_type: string;
  tier: string;
  billing_cycle: string;
  status: 'pending' | 'success' | 'failed' | 'refunded';
  created_at: string;
  completed_at?: string;
}

export interface InitiatePaymentRequest {
  module_type: 'mail' | 'template' | 'bundle';
  tier: 'standard' | 'pro' | 'unlimited';
  billing_cycle: 'monthly' | 'yearly';
}

export interface InitiatePaymentResponse {
  merchant_oid: string;
  iframe_token: string;
  amount: number;
  currency: string;
}

export interface FeatureAccessResponse {
  has_access: boolean;
  features: PlanFeatures | null;
}

// API Functions

/**
 * Tüm planları getirir
 */
export const getPlans = async (): Promise<PlansResponse> => {
  const response = await apiClient.get('/plans');
  return response.data.data;
};

/**
 * Belirli bir planı getirir
 */
export const getPlan = async (planId: string): Promise<Plan> => {
  const response = await apiClient.get(`/plans/${planId}`);
  return response.data.data.plan;
};

/**
 * Plan fiyatını getirir
 */
export const getPlanPrice = async (
  module: string,
  tier: string,
  billingCycle: 'monthly' | 'yearly' = 'monthly'
): Promise<{ plan_id: string; billing_cycle: string; price: number; price_tl: string }> => {
  const response = await apiClient.get('/plans/price', {
    params: { module, tier, billing_cycle: billingCycle },
  });
  return response.data.data;
};

/**
 * Ödeme başlatır ve PayTR iframe token döner
 */
export const initiatePayment = async (
  request: InitiatePaymentRequest
): Promise<InitiatePaymentResponse> => {
  const response = await apiClient.post('/payment/initiate', request);
  return response.data.data;
};

/**
 * Ödeme durumunu kontrol eder
 */
export const getPaymentStatus = async (merchantOid: string): Promise<Payment> => {
  const response = await apiClient.get(`/payment/status/${merchantOid}`);
  return response.data.data.payment;
};

/**
 * Kullanıcının ödeme geçmişini getirir
 */
export const getPaymentHistory = async (): Promise<Payment[]> => {
  const response = await apiClient.get('/payment/history');
  return response.data.data.payments || [];
};

/**
 * Kullanıcının tüm aboneliklerini getirir
 */
export const getUserSubscriptions = async (): Promise<Subscription[]> => {
  const response = await apiClient.get('/subscriptions');
  return response.data.data.subscriptions || [];
};

/**
 * Belirli bir modül için abonelik bilgisini getirir
 */
export const getSubscription = async (module: string): Promise<Subscription | null> => {
  try {
    const response = await apiClient.get(`/subscriptions/${module}`);
    return response.data.data.subscription;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Aboneliği iptal eder
 */
export const cancelSubscription = async (module: string): Promise<void> => {
  await apiClient.post(`/subscriptions/${module}/cancel`);
};

/**
 * Özellik erişimini kontrol eder
 */
export const checkFeatureAccess = async (module: string): Promise<FeatureAccessResponse> => {
  const response = await apiClient.get('/subscriptions/access', {
    params: { module },
  });
  return response.data.data;
};

/**
 * Fiyatı TL formatına çevirir
 */
export const formatPrice = (priceInKurus: number): string => {
  const tl = priceInKurus / 100;
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(tl);
};

/**
 * Plan özelliklerini insan tarafından okunabilir formata çevirir
 */
export const formatFeatureValue = (value: number | boolean): string => {
  if (typeof value === 'boolean') {
    return value ? 'Var' : 'Yok';
  }
  if (value === -1) {
    return 'Sınırsız';
  }
  return value.toString();
};

const paymentApi = {
  getPlans,
  getPlan,
  getPlanPrice,
  initiatePayment,
  getPaymentStatus,
  getPaymentHistory,
  getUserSubscriptions,
  getSubscription,
  cancelSubscription,
  checkFeatureAccess,
  formatPrice,
  formatFeatureValue,
};

export default paymentApi;

